/**
 * The application context shared by every view/component. Assembled once in
 * main.ts#createFrontend from the injected shared-package instances.
 */
import type { Locale, ThemeMode, ThemePreference } from "@mb-group/shared-types";
import type { I18n } from "@mb-group/i18n";
import type { Analytics, Auth, GqlClient } from "./deps.js";
import type { ContentGateway } from "../data/gateway.js";
import type { Router } from "./router.js";
import type { AppRouteKey, QueryParams, RouteParams } from "./routes.js";
import { getCopy, type SiteCopy } from "./copy.js";

/** Which lead-magnet the gate modal is showing, if any. */
export type GateTarget = number | "newsletter" | null;

export interface AppState {
	/** True when serving the offline DEMO fallback (gateway unreachable). */
	demo: boolean;
	themePreference: ThemePreference;
	theme: ThemeMode;
	/** Mobile burger menu. */
	menuOpen: boolean;
	/** Desktop "Usługi" mega menu. */
	megaOpen: boolean;
	/** The AI-assistant overlay (also the site search). */
	assistantOpen: boolean;
	assistantQuery: string;
	/** Lead-magnet gate modal. */
	gate: GateTarget;
	gateSent: boolean;
	loading: boolean;
}

/** Structural reactive store shape (compatible with @mb-group/ui-components createStore). */
export interface Store<T> {
	get(): T;
	set(patch: Partial<T> | ((state: T) => Partial<T>)): void;
	subscribe(cb: (state: T) => void): () => void;
}

export interface AppContext {
	readonly client: GqlClient;
	readonly auth: Auth;
	readonly analytics: Analytics;
	readonly i18n: I18n;
	readonly site: string;
	/** Strapi admin panel URL, when configured (linked from /panel). */
	readonly cmsUrl?: string;
	/** The content panel on its own host; /panel sends editors there when set. */
	readonly panelUrl?: string;
	readonly gateway: ContentGateway;
	readonly router: Router;
	readonly store: Store<AppState>;
	/** Current locale (mirrors i18n.locale). */
	locale(): Locale;
	/** Shared UI-chrome translations. */
	t: I18n["t"];
	/** App display copy for the current locale. */
	copy(): SiteCopy;
	/** Track a pseudonymised analytics event. */
	track(event: { type: string } & Record<string, unknown>): void;
	navigate(key: AppRouteKey, params?: RouteParams, query?: QueryParams): void;
	href(key: AppRouteKey, params?: RouteParams, query?: QueryParams): string;
	/** Open the assistant overlay, optionally pre-filling the query. */
	openAssistant(query?: string): void;
	/** Open the lead-magnet gate for a magnet index, or the newsletter. */
	openGate(target: GateTarget): void;
}

export function copyFor(locale: Locale): SiteCopy {
	return getCopy(locale);
}
